
import { useLanguage } from "@/contexts/LanguageContext";
import StepContainer from "../components/StepContainer";
import DurationSection from "../components/DurationSection";
import { Location } from "../types";
import { useEffect, useState, ReactNode } from "react";
import { formatDateToYYYYMMDD } from "@/hooks/campaign/services/db/utils/dateFormatter";
import { calculateDurationDays } from "@/hooks/campaign/utils/date/durationUtils";
import { useStepValidation } from "../hooks/useStepValidation";
import { Bot, Sparkles } from "lucide-react";
import { secureStorage } from "@/utils/secureStorage";

interface TimeStepProps {
  value: number;
  onChange: (values: number[]) => void;
  onNext: () => void;
  onBack: () => void;
  selectedLocations: Location[];
  aiAssistantButton?: ReactNode;
}

const TimeStep = ({ value, onChange, onNext, onBack, selectedLocations, aiAssistantButton }: TimeStepProps) => {
  const { t, language } = useLanguage();
  const { isTimeStepValid } = useStepValidation();
  const [isAIPrefilled, setIsAIPrefilled] = useState(false);
  
  // Real-time validation
  const isNextDisabled = !isTimeStepValid(value);
  
  // Check if dates were AI-prefilled
  useEffect(() => {
    const aiSuggestionData = secureStorage.get('aiSuggestionData');
    const aiSuggestionApplied = localStorage.getItem('aiSuggestionApplied');
    
    if (aiSuggestionData || aiSuggestionApplied === 'true') {
      setIsAIPrefilled(true);
    }
  }, []);
  
  // When the component renders, make sure we validate the dates in localStorage
  useEffect(() => {
    // Check selection mode - for specific dates, don't recalculate from range
    const selectionMode = localStorage.getItem('dateSelectionMode');
    if (selectionMode === 'specific') {
      // In specific dates mode, trust the value passed from the hook
      // Don't override with range calculation
      return;
    }
    
    // Only recalculate for range mode
    const startDate = localStorage.getItem('startDate');
    const endDate = localStorage.getItem('endDate');
    const startDateExact = localStorage.getItem('startDate_exact');
    const endDateExact = localStorage.getItem('endDate_exact');
    
    // Recalculate duration if we have both dates to ensure consistency
    if ((startDateExact && endDateExact) || (startDate && endDate)) {
      const start = startDateExact || startDate;
      const end = endDateExact || endDate;
      
      if (start && end) {
        const calculatedDays = calculateDurationDays(start, end);
        if (calculatedDays !== value) {
          onChange([calculatedDays]);
        }
      }
    }
  }, [value, onChange]);
  
  // Create a handler for the next button that ensures dates are properly saved
  const handleNext = () => {
    // Pass the value as an array - this will trigger instant save
    onChange([value]);
    
    const selectionMode = localStorage.getItem('dateSelectionMode');
    
    // For specific dates mode, save the selected dates count
    if (selectionMode === 'specific') {
      const formData = JSON.parse(localStorage.getItem('formData') || '{}');
      formData.duration = value;
      formData.dateSelectionMode = 'specific';
      localStorage.setItem('formData', JSON.stringify(formData));
      onNext();
      return;
    }
    
    // Get the current dates from localStorage - prioritize exact formats
    const startDateExact = localStorage.getItem('startDate_exact');
    const endDateExact = localStorage.getItem('endDate_exact');
    const startDate = localStorage.getItem('startDate');
    const endDate = localStorage.getItem('endDate');
    
    // If we have valid dates, update formData with them
    if ((startDateExact && endDateExact) || (startDate && endDate)) {
      try {
        // Get default dates (today and a week from today)
        const today = new Date();
        const defaultStart = today.toISOString().split('T')[0];
        const nextWeek = new Date();
        nextWeek.setDate(today.getDate() + 7);
        const defaultEnd = nextWeek.toISOString().split('T')[0];
        
        // Use exact dates if available, otherwise use the standard dates
        const formattedStartDate = startDateExact || formatDateToYYYYMMDD(startDate, defaultStart);
        const formattedEndDate = endDateExact || formatDateToYYYYMMDD(endDate, defaultEnd);
        
        // Update formData with the formatted dates
        const formData = JSON.parse(localStorage.getItem('formData') || '{}');
        formData.startDate = formattedStartDate;
        formData.endDate = formattedEndDate;
        formData.duration = value;
        
        // Save formData back to localStorage (instant save)
        localStorage.setItem('formData', JSON.stringify(formData));
      } catch (error) {
        // Error formatting dates
      }
    }
    
    onNext();
  };

  return (
    <StepContainer
      title={t("time")}
      description={t("selectDurationDesc")}
      onNext={handleNext}
      onBack={onBack}
      isNextDisabled={isNextDisabled}
      aiAssistantButton={aiAssistantButton}
    >
      {/* AI Prefilled Indicator */}
      {isAIPrefilled && value > 0 && (
        <div className="mb-4 p-3 rounded-lg bg-gradient-to-r from-primary/10 via-secondary/10 to-primary/10 border border-primary/20 flex items-center gap-3">
          <div className="flex items-center justify-center w-8 h-8 rounded-full bg-primary/20">
            <Bot className="h-4 w-4 text-primary" />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-foreground">
                {language === 'ar' ? 'تم تحديد المدة بواسطة الذكاء الاصطناعي' : 'Duration set by AI'}
              </span>
              <Sparkles className="h-3 w-3 text-primary animate-pulse" />
            </div>
            <p className="text-xs text-muted-foreground">
              {language === 'ar' 
                ? `${value} يوم - يمكنك تعديل التواريخ حسب الحاجة` 
                : `${value} days - You can adjust the dates as needed`}
            </p>
          </div>
        </div>
      )}
      
      <DurationSection 
        value={value} 
        onChange={(values) => {
          onChange(values);
        }} 
        selectedLocations={selectedLocations}
      />

    </StepContainer>
  );
};

export default TimeStep;
